import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Patient } from "../../types/patient.definition";
import {
  SearchRequest,
} from "../../types/search.definition";

interface PatientState {
  patients: Patient[];
  loading: boolean;
  error: string | null;
  facilityId: number | null;
  // Filters
  searchTerm: string;
  filterGender: string;
  filterAgeRange: string;
  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  // UI state
  showFilters: boolean;
  showPatientForm: boolean;
  showAppointmentForm: boolean;
  showPatientDetails: boolean;
  showAlertSettings: boolean;
  showMedicalRecords: boolean;
  showAppointments: boolean;
  selectedPatient: Patient | null;
  isCreatingPatient: boolean;
  isCreatingAppointment: boolean;
  newPatientId: string | null;
}

const initialState: PatientState = {
  patients: [],
  loading: false,
  error: null,
  facilityId: null,
  searchTerm: "",
  filterGender: "",
  filterAgeRange: "",
  currentPage: 1,
  itemsPerPage: 10,
  totalItems: 0,
  showFilters: false,
  showPatientForm: false,
  showAppointmentForm: false,
  showPatientDetails: false,
  showAlertSettings: false,
  showMedicalRecords: false,
  showAppointments: false,
  selectedPatient: null,
  isCreatingPatient: false,
  isCreatingAppointment: false,
  newPatientId: null,
};

const patientSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {
    fetchPatientsRequest: (
      state,
      action: PayloadAction<{
        facilityId: number;
        searchRequest: SearchRequest;
      }>
    ) => {
      // Only set loading if not already loading to prevent race conditions
      if (!state.loading) {
        state.loading = true;
        state.error = null;
      }
      state.facilityId = action.payload.facilityId;
    },
    fetchPatientsSuccess: (
      state,
      action: PayloadAction<{
        patients: Patient[];
        totalItems: number;
        paginationSettings: { limit: number; offset: number };
      }>
    ) => {
      state.patients = action.payload.patients;
      state.totalItems = action.payload.totalItems;
      state.loading = false;
      state.error = null;
    },
    fetchPatientsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when search changes
    },
    setFilterGender: (state, action: PayloadAction<string>) => {
      state.filterGender = action.payload;
      state.currentPage = 1; // Reset to first page when filter changes
    },
    setFilterAgeRange: (state, action: PayloadAction<string>) => {
      state.filterAgeRange = action.payload;
      state.currentPage = 1; // Reset to first page when filter changes
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when items per page changes
    },
    addPatientToList: (state, action: PayloadAction<Patient>) => {
      state.patients.unshift(action.payload);
      state.totalItems += 1;
    },
    updatePatientInList: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Patient> }>
    ) => {
      const index = state.patients.findIndex(
        (patient) => patient.id === action.payload.id
      );
      if (index !== -1) {
        state.patients[index] = {
          ...state.patients[index],
          ...action.payload.updates,
        };
      }
    },
    removePatientFromList: (state, action: PayloadAction<string>) => {
      state.patients = state.patients.filter(
        (patient) => patient.id !== action.payload
      );
      state.totalItems = Math.max(0, state.totalItems - 1);
    },
    setShowFilters: (state, action: PayloadAction<boolean>) => {
      state.showFilters = action.payload;
    },
    setShowPatientForm: (state, action: PayloadAction<boolean>) => {
      state.showPatientForm = action.payload;
    },
    setShowAppointmentForm: (state, action: PayloadAction<boolean>) => {
      state.showAppointmentForm = action.payload;
    },
    setShowPatientDetails: (state, action: PayloadAction<boolean>) => {
      state.showPatientDetails = action.payload;
    },
    setShowAlertSettings: (state, action: PayloadAction<boolean>) => {
      state.showAlertSettings = action.payload;
    },
    setShowMedicalRecords: (state, action: PayloadAction<boolean>) => {
      state.showMedicalRecords = action.payload;
    },
    setShowAppointments: (state, action: PayloadAction<boolean>) => {
      state.showAppointments = action.payload;
    },
    setSelectedPatient: (state, action: PayloadAction<Patient | null>) => {
      state.selectedPatient = action.payload;
    },
    setIsCreatingPatient: (state, action: PayloadAction<boolean>) => {
      state.isCreatingPatient = action.payload;
    },
    setIsCreatingAppointment: (state, action: PayloadAction<boolean>) => {
      state.isCreatingAppointment = action.payload;
    },
    setNewPatientId: (state, action: PayloadAction<string | null>) => {
      state.newPatientId = action.payload;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.filterGender = "";
      state.filterAgeRange = "";
      state.currentPage = 1;
    },
    clearPatients: (state) => {
      state.patients = [];
      state.totalItems = 0;
      state.error = null;
    },
  },
});

export const {
  fetchPatientsRequest,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  setSearchTerm,
  setFilterGender,
  setFilterAgeRange,
  setCurrentPage,
  setItemsPerPage,
  addPatientToList,
  updatePatientInList,
  removePatientFromList,
  setShowFilters,
  setShowPatientForm,
  setShowAppointmentForm,
  setShowPatientDetails,
  setShowAlertSettings,
  setShowMedicalRecords,
  setShowAppointments,
  setSelectedPatient,
  setIsCreatingPatient,
  setIsCreatingAppointment,
  setNewPatientId,
  resetFilters,
  clearPatients,
} = patientSlice.actions;

export default patientSlice.reducer;

